// ANGEL FLIGHT — FiringSystem: the door gun (first-person depth raycast).
//
// Reads the gunner's ACTIVE weapon each shot (keys 1-4 switch it; per-weapon
// ammo/reload state persists across switches — no free instant reload). On each
// shot: gate by fire-rate + ammo, jitter the aim by spread, then pick the
// NEAREST live enemy (the 3D raycast) as the PRIMARY target — gated by the
// weapon's rangeZ (a hit beyond it falls short). Per-weapon identity then
// layers extra targets on top in SCREEN space + a DEPTH term (screen px alone
// is depth-blind at the horizon):
//   • minigun     — plain single-target hose (thin tracer).
//   • autocannon  — primary + a small AoE blast (every enemy within blastRadiusPx
//                   of the primary's screen centre AND within blastDepthM of its
//                   depth), fat tracer, big shake.
//   • railgun     — PIERCE: every live enemy near the muzzle→aim screen line
//                   (piercing along depth IS its identity), beam tracer, brutal
//                   recoil, slow.
//   • flamer      — wide cone: every enemy within coneSpreadPx of the AIM point
//                   AND within rangeZ metres (short-range volume), flame tracer,
//                   minimal recoil.
// Head zone = the upper band of the raycast hit (primary only); splash targets
// take body damage. Damage/zone/kill/economy reuse the same per-hit flow + events.
// Friendly fire counts debounced STRIKES (see hitFriendly), not rounds.

import { BALANCE } from '../config/balance';
import { Cooldown } from '../core/timers';
import {
  getWeapon,
  fireIntervalMs,
  shotDamage,
  effectiveAmmo,
  reloadDurationMs,
  withinRadiusPx,
  distToSegmentPx,
} from '../config/weapons';
import { getEnemy, zoneMult, isFriendly } from '../config/enemies';
import type { BodyZone, GameContext, GameSystem, LiveEnemy } from './System';
import type { Vec2, WeaponDef, WeaponId, WorldPos } from '../core/types';

/** Persisted per-weapon magazine + reload progress (survives switches). */
interface MagState {
  ammo: number;
  reloading: boolean;
  reloadLeft: number;
}

export class FiringSystem implements GameSystem {
  private ctx!: GameContext;
  /** The weapon we last set ammo/cap for; resynced when activeWeapon changes. */
  private weaponId: WeaponId = 'minigun';
  private readonly cd = new Cooldown();
  private ammo = 0;
  private cap = 0;
  private reloading = false;
  private reloadLeft = 0;
  /** Per-weapon ammo/reload state stashed on switch-away and restored on
   *  switch-back — switching is NEVER a free instant reload. A holstered
   *  weapon's reload is FROZEN (it resumes when re-equipped). */
  private readonly mags = new Map<WeaponId, MagState>();
  /** Friendly-fire strike debounce: >0 while rounds merge into the last counted
   *  strike (does NOT refresh on merged rounds — sustained fire still accrues
   *  one strike per window). */
  private ffStrikeWindowLeftMs = 0;
  /** Optional hook the WorldView sets so we can flash a hit enemy. */
  flashEnemy?: (id: number, ms: number) => void;

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.weaponId = ctx.gunner?.activeWeapon ?? 'minigun';
    const def = getWeapon(this.weaponId);
    this.cap = effectiveAmmo(def, ctx.stats);
    this.ammo = this.cap;
    ctx.bus.emit('ammo:changed', { ammo: this.ammo, cap: this.cap });
  }

  /** Re-emit the current weapon + ammo state. The HUD binds AFTER init (the bus
   *  is created with the loop), so without this the ammo readout shows '—' and
   *  the weapon chrome lies until the first shot/switch. */
  announce(): void {
    this.ctx.bus.emit('weapon:switched', { weapon: this.weaponId });
    this.ctx.bus.emit('ammo:changed', { ammo: this.ammo, cap: this.cap });
    if (this.reloading) {
      this.ctx.bus.emit('weapon:reloadStart', { weapon: this.weaponId, durationMs: this.reloadLeft });
    }
  }

  update(dt: number): void {
    this.cd.tick(dt);
    if (this.ffStrikeWindowLeftMs > 0) this.ffStrikeWindowLeftMs -= dt;
    const g = this.ctx.gunner;
    if (!g) return;

    // A weapon switch swaps to that gun's OWN magazine/reload state.
    if (g.activeWeapon !== this.weaponId) this.equip(g.activeWeapon);

    if (this.reloading) {
      this.reloadLeft -= dt;
      if (this.reloadLeft <= 0) {
        this.reloading = false;
        this.ammo = this.cap;
        this.ctx.bus.emit('weapon:reloadEnd', { weapon: this.weaponId });
        this.ctx.bus.emit('ammo:changed', { ammo: this.ammo, cap: this.cap });
      }
      return;
    }

    if (!(g.firing && g.spinup >= 1) || !this.cd.ready) return;
    if (this.ammo <= 0) {
      this.startReload();
      return;
    }
    this.fireOnce(g.aim, g.muzzle());
    this.cd.set(fireIntervalMs(getWeapon(this.weaponId), this.ctx.stats));
  }

  /** Switch to a new weapon. Per-weapon ammo + reload progress PERSISTS across
   *  switches (stash the outgoing state, restore the incoming one — first equip
   *  of a gun starts with a full magazine). The fire cooldown is NOT reset, so
   *  rapid switch-cycling can't cheat a slow gun's fire interval either. */
  private equip(id: WeaponId): void {
    this.mags.set(this.weaponId, {
      ammo: this.ammo,
      reloading: this.reloading,
      reloadLeft: this.reloadLeft,
    });
    this.weaponId = id;
    const def = getWeapon(id);
    this.cap = effectiveAmmo(def, this.ctx.stats);
    const prev = this.mags.get(id);
    if (prev) {
      this.ammo = Math.min(prev.ammo, this.cap);
      this.reloading = prev.reloading;
      this.reloadLeft = prev.reloadLeft;
    } else {
      this.ammo = this.cap;
      this.reloading = false;
      this.reloadLeft = 0;
    }
    this.ctx.bus.emit('weapon:switched', { weapon: id });
    this.ctx.bus.emit('ammo:changed', { ammo: this.ammo, cap: this.cap });
    // Resuming a frozen vent: re-announce so the HUD shows VENTING… again.
    if (this.reloading) {
      this.ctx.bus.emit('weapon:reloadStart', { weapon: id, durationMs: this.reloadLeft });
    }
  }

  private startReload(): void {
    const def = getWeapon(this.weaponId);
    this.reloading = true;
    this.reloadLeft = reloadDurationMs(def, this.ctx.stats);
    this.ctx.bus.emit('weapon:dry', { weapon: this.weaponId });
    this.ctx.bus.emit('weapon:reloadStart', { weapon: this.weaponId, durationMs: this.reloadLeft });
  }

  private fireOnce(aim: Vec2, muzzle: Vec2): void {
    const ctx = this.ctx;
    const def = getWeapon(this.weaponId);
    this.ammo -= 1;
    ctx.run.shotsFired += 1;
    ctx.bus.emit('ammo:changed', { ammo: this.ammo, cap: this.cap });
    ctx.gunner?.kick();
    const recoil = def.recoilScale ?? 1;
    ctx.bus.emit('weapon:fired', { weapon: this.weaponId, at: muzzle, recoil });
    ctx.bus.emit('fx:muzzle', { at: muzzle, angle: -Math.PI / 2 });
    ctx.bus.emit('fx:shake', { trauma: BALANCE.juice.shakePerShot * recoil });

    // Jitter the shot point by spread, then raycast it into the 3D scene — the
    // geometrically-correct hit test for the primary target at the right depth.
    const jx = (ctx.rng.next() * 2 - 1) * def.spreadPx;
    const jy = (ctx.rng.next() * 2 - 1) * def.spreadPx;
    const shot: Vec2 = { x: aim.x + jx, y: aim.y + jy };

    // Range gate (depth-aware): a raycast hit BEYOND the weapon's rangeZ falls
    // short — treat it as a clean miss (the flamer cannot snipe the horizon).
    let hit = ctx.view.raycastEnemy(shot);
    if (hit && hit.world.z > def.rangeZ) hit = null;
    const primary = hit ? this.findEnemy(hit.id) : null;
    // Tracer streaks from the muzzle to the TRUE 3D impact (hit.world) when we
    // hit — so it ends at the enemy at the right depth, not re-projected onto the
    // ground. On a miss it runs to the screen shot point. tracerStyle lets the
    // render layer draw the right line (thin/fat/beam/flame).
    const impact: Vec2 = primary ? { x: primary.sx, y: primary.sy } : shot;
    ctx.bus.emit('fx:tracer', { from: muzzle, to: impact, toWorld: hit?.world, style: def.tracerStyle });

    // Resolve the PRIMARY hit (with its true raycast zone) first.
    const struck = new Set<number>();
    let hostileHit = false;
    if (hit && primary) {
      hostileHit = this.applyHit(def, primary, hit.zone, shot, hit.world) || hostileHit;
      struck.add(primary.id);
    }

    // Then layer the per-weapon extra targets in screen space. These are ALWAYS
    // body-zone hits (no per-target raycast); they reuse the full damage/kill/
    // economy flow so secondary kills pay out exactly like primaries.
    hostileHit = this.applySplash(def, aim, muzzle, primary, struck) || hostileHit;

    // Accuracy: a round "hits" when it damaged at least one HOSTILE (friendly
    // strikes are penalised separately and never count as accurate fire).
    if (hostileHit) ctx.run.shotsHit += 1;
  }

  /** Pick + damage the weapon's secondary targets (blast / pierce / cone).
   *  Selection is screen-space PLUS a depth term per weapon (screen px alone is
   *  depth-blind at the horizon, where 150px spans the battlefield):
   *   • blast — candidates must sit within blastDepthM of the primary's depth,
   *   • cone  — candidates beyond rangeZ are out of the flames' reach,
   *   • pierce — the lance intentionally pierces ALONG depth (its identity),
   *     gated only by rangeZ.
   *  Returns true if any HOSTILE took damage (accuracy bookkeeping). */
  private applySplash(
    def: WeaponDef,
    aim: Vec2,
    muzzle: Vec2,
    primary: LiveEnemy | null,
    struck: Set<number>,
  ): boolean {
    let hostileHit = false;

    // Autocannon blast: enemies near the PRIMARY's screen centre (needs a hit)
    // AND near the primary's world DEPTH (the shell explodes at that depth).
    if (def.blastRadiusPx && def.blastRadiusPx > 0 && primary) {
      for (const e of this.ctx.enemies) {
        if (e.dead || struck.has(e.id)) continue;
        if (def.blastDepthM !== undefined && Math.abs(e.pos.z - primary.pos.z) > def.blastDepthM)
          continue;
        if (withinRadiusPx(e.sx, e.sy, primary.sx, primary.sy, def.blastRadiusPx)) {
          hostileHit =
            this.applyHit(def, e, 'body', { x: e.sx, y: e.sy }, this.enemyWorld(e)) || hostileHit;
          struck.add(e.id);
        }
      }
    }

    // Railgun pierce: every enemy near the muzzle→aim screen line (no hit needed
    // — the lance flies through whether or not it found a primary). The beam does
    // not STOP at the reticle: extend the endpoint far past the aim along the
    // muzzle→aim direction so it keeps piercing into the depth of the scene.
    if (def.pierce) {
      const reach = def.hitRadiusPx;
      const dx = aim.x - muzzle.x;
      const dy = aim.y - muzzle.y;
      const len = Math.hypot(dx, dy) || 1;
      const far = 4000; // px — well past the far plane in the logical view
      const endX = muzzle.x + (dx / len) * far;
      const endY = muzzle.y + (dy / len) * far;
      for (const e of this.ctx.enemies) {
        if (e.dead || struck.has(e.id) || e.pos.z > def.rangeZ) continue;
        if (distToSegmentPx(e.sx, e.sy, muzzle.x, muzzle.y, endX, endY) <= reach) {
          hostileHit =
            this.applyHit(def, e, 'body', { x: e.sx, y: e.sy }, this.enemyWorld(e)) || hostileHit;
          struck.add(e.id);
        }
      }
    }

    // Flamer cone: every enemy within coneSpreadPx of the AIM point — but ONLY
    // within rangeZ metres (the flames fall short past that; the cone is a
    // short-range volume, never a horizon-spanning screen circle).
    if (def.coneSpreadPx && def.coneSpreadPx > 0) {
      for (const e of this.ctx.enemies) {
        if (e.dead || struck.has(e.id) || e.pos.z > def.rangeZ) continue;
        if (withinRadiusPx(e.sx, e.sy, aim.x, aim.y, def.coneSpreadPx)) {
          hostileHit =
            this.applyHit(def, e, 'body', { x: e.sx, y: e.sy }, this.enemyWorld(e)) || hostileHit;
          struck.add(e.id);
        }
      }
    }

    return hostileHit;
  }

  /** Apply damage to one enemy in a given zone and emit the hit/kill/economy
   *  events. Shared by the primary raycast hit and every splash target.
   *  Returns true when HOSTILE damage was applied (false on the friendly
   *  penalty path — friendly hits are never "accurate" fire). */
  private applyHit(
    def: WeaponDef,
    e: LiveEnemy,
    zone: BodyZone,
    impact: Vec2,
    world: WorldPos | undefined,
  ): boolean {
    const ctx = this.ctx;
    const eDef = getEnemy(e.def);

    // BLUEFOR friendly (AC-130 "DON'T HIT FRIENDLIES"): NO kill/bounty/combo.
    // Route to the penalty path instead — break combo + dock SCRIP (EconomySystem
    // listens to friendly:hit) + count a STRIKE + maybe fail the mission.
    if (isFriendly(eDef)) {
      this.hitFriendly(def, e, impact, world);
      return false;
    }

    const dmg = shotDamage(def, ctx.stats) * zoneMult(eDef, zone);
    e.hp -= dmg;
    this.flashEnemy?.(e.id, BALANCE.juice.hitFlashMs);
    const headshot = zone === 'head';
    const kill = e.hp <= 0;
    // Lateral shot direction at the target (for a directional flinch): where the
    // shot entered relative to the enemy centre, in [-1, 1].
    const dirX = Math.max(-1, Math.min(1, (impact.x - e.sx) / 80));

    ctx.bus.emit('hit:landed', { targetId: e.id, zone, damage: dmg, at: impact, world, dirX, kill });
    ctx.bus.emit('fx:blood', { at: impact, scale: 0.6 + e.scale * 6, world });
    ctx.bus.emit('fx:hitmarker', { at: impact, kill, headshot });

    if (kill) {
      e.dead = true;
      if (headshot) ctx.run.headshots += 1;
      ctx.bus.emit('enemy:killed', {
        id: e.id,
        def: e.def,
        at: impact,
        world,
        bounty: eDef.bounty,
        headshot,
      });
      ctx.bus.emit('fx:shake', { trauma: BALANCE.juice.shakeOnKill });
      ctx.bus.emit('fx:hitstop', { ms: headshot ? BALANCE.juice.hitstopHeadshotMs : BALANCE.juice.hitstopKillMs });
    }
    return true;
  }

  /** A round struck a BLUEFOR friendly. This is the PENALTY path — never a kill.
   *  It still damages the friendly so it can fall, but it pays NOTHING.
   *
   *  STRIKES, not rounds: every friendly round within
   *  BALANCE.friendlyFire.strikeWindowMs of a counted strike MERGES into it
   *  (one trigger-sweep at 17rps = one strike, not three). A new strike
   *  increments `friendliesHit`, emits `friendly:hit` (EconomySystem breaks the
   *  combo + docks SCRIP off this), and:
   *   • at threshold − 1 strikes emits `friendly:warning` (the CHECK FIRE
   *     klaxon + HUD callout — the final strike is never a surprise),
   *   • at the threshold fails the mission with reason 'FRIENDLY FIRE'.
   *  Sustained careless fire still fails — the window does not refresh on
   *  merged rounds, so a held hose accrues one strike per window.
   *  When the friendly's HP runs out it emits `friendly:down` (a casualty, NOT
   *  an `enemy:killed` frag) regardless of strike merging. */
  private hitFriendly(
    def: WeaponDef,
    e: LiveEnemy,
    impact: Vec2,
    world: WorldPos | undefined,
  ): void {
    const ctx = this.ctx;
    const dmg = shotDamage(def, ctx.stats);
    e.hp -= dmg;
    this.flashEnemy?.(e.id, BALANCE.juice.hitFlashMs);
    const dirX = Math.max(-1, Math.min(1, (impact.x - e.sx) / 80));
    ctx.bus.emit('fx:blood', { at: impact, scale: 0.6 + e.scale * 6, world });

    // Down the friendly (a casualty, not a frag) when its HP is gone.
    if (e.hp <= 0 && !e.dead) {
      e.dead = true;
      ctx.bus.emit('friendly:down', { id: e.id, at: impact, world });
    }

    // Debounce: rounds inside the burst window merge into the counted strike.
    if (this.ffStrikeWindowLeftMs > 0) return;
    this.ffStrikeWindowLeftMs = BALANCE.friendlyFire.strikeWindowMs;

    ctx.run.friendliesHit += 1;
    ctx.bus.emit('friendly:hit', { id: e.id, at: impact, world, dirX });

    const left = BALANCE.friendlyFire.failThreshold - ctx.run.friendliesHit;
    if (left === 1) {
      // Penultimate strike: warn BEFORE the final strike can end the mission.
      ctx.bus.emit('friendly:warning', { strikes: ctx.run.friendliesHit, strikesLeft: left });
    } else if (left <= 0) {
      // Target discipline failed → abort the mission.
      ctx.bus.emit('mission:failed', { missionId: ctx.mission.id, reason: 'FRIENDLY FIRE' });
    }
  }

  /** World point for a screen-selected splash target (its rig pos), so blood/
   *  kill VFX land at the right depth for AoE/pierce/cone hits. */
  private enemyWorld(e: LiveEnemy): WorldPos {
    return { x: e.pos.x, y: e.pos.y, z: e.pos.z };
  }

  private findEnemy(id: number): LiveEnemy | null {
    for (const e of this.ctx.enemies) if (e.id === id && !e.dead) return e;
    return null;
  }

  destroy(): void {
    /* no subscriptions */
  }
}
